/*
---
covers:
  - frontend/src/pages/sgsst/matriz_legal/matriz-legal.tsx
  - frontend/src/pages/sgsst/expediente/expediente-matriz-legal.tsx
  - frontend/src/hooks/sgsst/matriz_legal/use-matriz-legal.ts
  - backend/app/sgsst/matriz_legal/routers/sgsst_matriz_legal.py
  - backend/app/sgsst/matriz_legal/core/legal_matrix.py
revisado: 2026-09-28
---
*/
import type {ManualSeccion} from '../types'

export const seccion: ManualSeccion = {
  id: 'matriz-legal',
  titulo: 'Matriz legal',
  resumen:
    'El registro de las normas de riesgos laborales que aplican a tu empresa: Kairos detecta novedades y tú decides incorporarlas o descartarlas; nada se agrega solo.',
  enCorto: [
    'Las normas de riesgos laborales que aplican',
    'Incorpore o descarte cada novedad detectada',
    'Lo incumplido pasa al Plan de mejoramiento',
  ],
  replayId: 'matriz-legal',
  operaciones: [
    {
      id: 'incorporar-novedad',
      titulo: 'Incorporar una norma nueva al registro',
      como: 'La matriz arranca en la pestaña «Novedades» cuando hay pendientes: cada norma detectada muestra su título y el botón «Incorporar al registro».',
      porQueImporta:
        'Kairos detecta las normas que ya aplican a tu empresa por tus hechos (CIIU, clase de riesgo, peligros confirmados) pero nunca las agrega sola — incorporar es tu decisión, con la fecha y quién la tomó quedando registradas.',
    },
    {
      id: 'buscar-en-el-registro',
      titulo: 'Encontrar una norma en el registro',
      como: 'El registro se muestra de a quince normas, con «Anterior»/«Siguiente» al pie. El buscador de arriba encuentra por referencia («0312»), por título o por quien la expidió.',
      porQueImporta:
        'En una constructora el registro llega a decenas de normas: buscar es más rápido que pasear páginas, y el pie te dice siempre cuántas hay en total.',
    },
    {
      id: 'ver-procedencia',
      titulo: 'Ver la procedencia de una norma incorporada',
      como: 'En la pestaña «Registro», cada fila tiene un enlace «Ver la norma y su procedencia» que despliega los artículos clave y la caja de procedencia: la regla que la disparó y los hechos de tu empresa en ese momento (riesgo, trabajadores, CIIU, fecha).',
      porQueImporta:
        'La procedencia queda congelada al momento de incorporar — si tu clase de riesgo cambia después, la norma no se recalcula sola; ves exactamente por qué aplicaba cuando la incorporaste.',
    },
    {
      id: 'marcar-cumple-no-cumple',
      titulo: 'Marcar una norma «Cumple» o «No cumple»',
      como: 'Cada fila del registro tiene los botones «Cumple» y «No cumple» — marcar «No cumple» exige una justificación, que Kairos pre-llena para que la edites.',
      porQueImporta:
        'El estado de cada norma alimenta directamente tu exposición legal: una norma en «No cumple» es un hallazgo que debe llegar a tu plan de mejoramiento, no quedarse enterrado en una tabla.',
    },
    {
      id: 'crear-accion-de-mejora',
      titulo: 'Llevar una norma «No cumple» a tu plan de mejoramiento',
      como: 'Una fila marcada «No cumple» muestra debajo el botón «Crear acción de mejora». Al tocarlo se abre el formulario de la acción ya escrito por Kairos —la descripción que cita la norma, el representante legal como responsable y 30 días de plazo— con la norma que la origina en un recuadro arriba. Cambias lo que quieras (quién, para cuándo, cómo se llama, con qué recursos) y confirmas.',
      porQueImporta:
        'Es el paso que cierra el círculo: la norma incumplida deja de ser un renglón en una tabla y pasa a ser trabajo con responsable y fecha — y sale ya configurada, sin tener que ir al plan a arreglarla después. Una vez creada, la fila deja de ofrecer el botón: en su lugar muestra la acción, cómo va y un enlace que te lleva directo a ella en el plan, resaltada. En el plan, la acción muestra el chip de la norma que cierra, y ese chip te trae de vuelta a esta fila.',
    },
    {
      id: 'ver-fuente-oficial',
      titulo: 'Ir al texto oficial de la norma',
      como: 'Dentro de «Ver la norma y su procedencia», debajo de la caja de procedencia, el enlace «Ver fuente oficial» abre el texto de la norma en el sitio del Estado, en una pestaña nueva.',
      porQueImporta:
        'Ante un inspector o una ARL, la cita no basta: poder abrir la fuente primaria en el momento es lo que hace defendible el registro.',
    },
    {
      id: 'declarar-norma-propia',
      titulo: 'Declarar una norma que Kairos no propuso',
      como: 'En la pestaña «Registro», junto al conteo, está «Declarar norma propia». El formulario pide el tipo, número, año y título de la norma, quién la expidió, opcionalmente el enlace a su texto, y —obligatorio— por qué le aplica a tu empresa. La norma queda en el registro con un sello «Propia», y desde su fila puedes editarla o retirarla.',
      porQueImporta:
        'Kairos deriva lo nacional, pero hay normas que solo tú sabes que te aplican: una ordenanza de tu municipio, un permiso de tu sector, una cláusula de SST que te exige un cliente. La explicación es obligatoria porque es lo que las normas derivadas traen gratis de la ley: sin ella, esa fila no podría defenderse ante un auditor. Si tecleas una norma que Kairos ya tiene en su catálogo, te lo dice y te lleva a «Novedades» para incorporarla con su cita legal — queda mejor que escrita a mano.',
    },
    {
      id: 'ligar-peligro',
      titulo: 'Decir qué peligros de tu matriz obliga a controlar esta norma',
      como: 'Cada fila del registro tiene la línea «Peligros que la citan». El botón «+ Ligar peligro» abre un buscador con los peligros de TU matriz de riesgos; eliges uno y queda ligado. Cada peligro ligado aparece como un chip con su nivel de riesgo y te lleva, al tocarlo, a esa fila de la matriz resaltada. La «×» del chip quita el vínculo.',
      porQueImporta:
        'Antes, la matriz de riesgos solo podía decir «este peligro tiene requisito legal» sin decir cuál. Ligarlos hace la relación auditable en los dos sentidos: desde la norma ves qué peligros te obliga a controlar, y desde el peligro ves con qué norma lo defiendes. Si retiras la norma del registro, Kairos te dice cuántos peligros la citan antes de confirmar, y esos peligros quedan marcados como «requisito sin especificar» — nunca apuntando a algo que ya no está.',
    },
    {
      id: 'descartar-novedad',
      titulo: 'Descartar una novedad que ya no aplica',
      como: 'Cuando una norma deja de aplicar (por ejemplo, tras cambiar de actividad), aparece como novedad de tipo «ya no aplica» con el botón «Descartar»; el modal exige un motivo antes de confirmar.',
      porQueImporta:
        'Una norma incorporada nunca se quita sola — el retiro siempre pasa por tu decisión explícita, con motivo, para que el registro siga siendo defendible ante una auditoría.',
    },
  ],
}
